import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import to from "await-to-js";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import cn from "clsx";
import withHeaderFooter, { IWithHeaderFooterProps } from "@/app/csr-src/hocs/with-header-footer";
import Recommend from "@/common-components-src/js/recommend/index";
import UKPageLoading from "@/common-components-src/components/UKPageLoading";
import { addCartUtil } from "@/app/csr-src/utils/cartUtils";

import { fetchRecordDetail } from "@/app/csr-src/apis/afterSales/afterSales.api";
import { RecordDetailResVo, IRecordDetailReq } from "@/app/csr-src/apis/afterSales/afterSales.interface";

import EmptyPage from "@/app/csr-src/pages/order/components/emptyPage";
import ErrorPage from "@/app/csr-src/pages/order/components/errorPage";

import StepBar from "./components/stepBar";
import StatusArea from "./components/statusArea";
import DetailInfo from "./components/detailInfo";
import RefundDetails from "./components/refundDetails";
import DetailProduct from "./components/detailProduct";
import RefundInformation from "./components/refundInformation";
import RuleWrap from "@/app/csr-src/pages/order/components/ruleWrap";

import styles from "./index.module.scss";
// 埋点
import { sendPv } from "@/tracks/25328";
import AfterSalesAccordion from "./components/afterSalesAccordion";

const AfterSales = withHeaderFooter(
  observer(({ JDIHeader }: IWithHeaderFooterProps) => {
    const [afsDetails, setAfsDetails] = useState<RecordDetailResVo | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const afsServiceId = queryParams.get("afsServiceId") || ""; // 获取服务单ID
    const orderId = queryParams.get("orderId") || ""; // 获取订单ID

    const loadAfsDetails = async () => {
      const requestData: IRecordDetailReq = {
        afsServiceId: String(afsServiceId),
        orderId: String(orderId),
      };
      const [err, recordDetailData] = await to(fetchRecordDetail(requestData));

      if (err) {
        setError("Failed to fetch order details");
        // setAfsDetails(null);
      } else {
        console.log("sxt-recordDetailData", recordDetailData);

        setAfsDetails(recordDetailData as RecordDetailResVo);
        setError(null);
      }

      setLoading(false);
      setIsLoaded(true);
    };

    useEffect(() => {
      // 退货详情页浏览埋点
      sendPv.Return_Details();
      loadAfsDetails();
    }, [afsServiceId]);

    const initCartNumHandler = () => {
      JDIHeader?.current?.initCartNum();
    };

    useEffect(() => {
      window.scrollTo(0, 0); // 页面跳转时滚动到顶部
    }, []);

    if (loading) {
      return <UKPageLoading style={{ backgroundColor: "transparent" }} text="Loading" />;
    }

    if (error) {
      return <ErrorPage />;
    }

    if (!afsDetails) {
      return <EmptyPage />;
    }

    const recommendAddCart = (item: { skuId: string }) => {
      addCartUtil([{ skuId: item?.skuId, num: "1" }], initCartNumHandler);
    };

    return (
      <div className={styles.afs_detail_wrap}>
        <Helmet>
          <title>{`Request a Refund |  #${orderId} | Joybuy`}</title>
          <meta
            name="description"
            content="Your refund request is being processed. Check the status and enjoy a seamless shopping experience at Joybuy.com with fast shipping and a delivery guarantee."
          />
        </Helmet>
        <div className={styles.detail_inner_wrap}>
          {!!afsDetails?.afsServiceProcessNodeVo?.nodeStatusList?.length && (
            <>
              <div
                className={cn(styles.step_outer_wrap, {
                  [styles.step_outer_wrap3]: afsDetails?.afsServiceProcessNodeVo?.nodeStatusList?.length === 2,
                  [styles.step_outer_wrap2]: afsDetails?.afsServiceProcessNodeVo?.nodeStatusList?.length === 3,
                })}
              >
                <StepBar step={afsDetails?.afsServiceProcessNodeVo?.currentIndex || 0} stepList={afsDetails?.afsServiceProcessNodeVo?.nodeStatusList || []} />
              </div>
              <RuleWrap />
              {/* 新增样式：进度条和展示退款金额和优惠券 */}
              <div className={styles.afterSalesAccordion_wrap}>
                {/* <AfterSalesAccordion statusList={afsDetails?.afsServiceProcessNodeVo?.nodeStatusList || []} /> */}
                <AfterSalesAccordion
                  afsDetailInfo={afsDetails?.afsServiceDetailVo}
                  afsProcessNode={afsDetails?.afsServiceProcessNodeVo}
                  onRefresh={loadAfsDetails}
                />
                {afsDetails?.afsServiceDetailVo?.refundAmountStr && <RefundDetails afsDetailInfo={afsDetails?.afsServiceDetailVo} />}
              </div>
            </>
          )}

          <DetailProduct wareInfoDTOList={afsDetails?.afsServiceDetailVo?.skuList} />
          {/* 售后详情，展示上传图片和视频 */}
          <RefundInformation afsDetailInfo={afsDetails?.afsServiceDetailVo} />
        </div>
        {isLoaded && <Recommend sceneCode="after_sales" addCartFn={recommendAddCart} />}
      </div>
    );
  }),
);

export default AfterSales;
